function asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @typedef {{
 *   id: string,
 *   required: boolean,
 *   sectionKey: "when" | "location" | "who" | "meta" | "recurrence" | "expert" | "rate",
 *   labelKey: string,
 *   hintKey?: string,
 * }} PlanningSessionCreateFieldDef
 */

/** @type {readonly PlanningSessionCreateFieldDef[]} */
export const PLANNING_SESSION_CREATE_FIELD_DEFS = Object.freeze([
  {
    id: "sessionDate",
    required: true,
    sectionKey: "when",
    labelKey: "trainerstunden.planning.createFields.sessionDate",
  },
  {
    id: "startTime",
    required: true,
    sectionKey: "when",
    labelKey: "trainerstunden.planning.createFields.startTime",
  },
  {
    id: "duration",
    required: true,
    sectionKey: "when",
    labelKey: "trainerstunden.planning.createFields.duration",
  },
  {
    id: "location",
    required: false,
    sectionKey: "location",
    labelKey: "trainerstunden.planning.createFields.location",
  },
  {
    id: "trainer",
    required: true,
    sectionKey: "who",
    labelKey: "trainerstunden.planning.createFields.trainer",
  },
  {
    id: "targetGroup",
    required: false,
    sectionKey: "who",
    labelKey: "trainerstunden.planning.createFields.targetGroup",
  },
  {
    id: "sessionFocus",
    required: false,
    sectionKey: "who",
    labelKey: "trainerstunden.planning.createFields.sessionFocus",
    hintKey: "trainerstunden.planning.createFields.sessionFocusHint",
  },
  {
    id: "participants",
    required: true,
    sectionKey: "who",
    labelKey: "trainerstunden.planning.createFields.participants",
  },
  {
    id: "assistant",
    required: false,
    sectionKey: "who",
    labelKey: "trainerstunden.planning.createFields.assistant",
  },
  {
    id: "recurrence",
    required: false,
    sectionKey: "recurrence",
    labelKey: "trainerstunden.planning.createFields.recurrence",
  },
  {
    id: "trainingType",
    required: false,
    sectionKey: "expert",
    labelKey: "trainerstunden.planning.createFields.trainingType",
  },
  {
    id: "courtFormat",
    required: false,
    sectionKey: "expert",
    labelKey: "trainerstunden.planning.createFields.courtFormat",
  },
  {
    id: "notes",
    required: false,
    sectionKey: "expert",
    labelKey: "trainerstunden.planning.createFields.notes",
  },
  {
    id: "rate",
    required: false,
    sectionKey: "rate",
    labelKey: "trainerstunden.planning.createFields.rate",
    hintKey: "trainerstunden.planning.createFields.rateHint",
  },
]);

/** @type {readonly string[]} */
export const PLANNING_SESSION_CREATE_SECTION_ORDER = Object.freeze([
  "when",
  "location",
  "who",
  "meta",
  "recurrence",
  "expert",
  "rate",
]);

const REQUIRED_IDS = new Set(
  PLANNING_SESSION_CREATE_FIELD_DEFS.filter((row) => row.required).map(
    (row) => row.id,
  ),
);

/** Felder, die in der vereinfachten Ansicht standardmäßig sichtbar sind. */
const DEFAULT_STANDARD_FIELD_IDS = new Set([
  "location",
  "participants",
  "assistant",
]);

/**
 * @returns {Record<string, boolean>}
 */
export function defaultPlanningSessionCreateFieldVisibility() {
  /** @type {Record<string, boolean>} */
  const out = {};
  for (const row of PLANNING_SESSION_CREATE_FIELD_DEFS) {
    // Trainingsart wird aus der Spieleranzahl abgeleitet — nicht im Anlage-Formular anzeigen.
    out[row.id] = row.id !== "trainingType";
  }
  return out;
}

/**
 * @param {unknown} raw
 * @returns {Record<string, boolean>}
 */
/**
 * @returns {Record<string, boolean>}
 */
export function defaultPlanningSessionCreateStandardFields() {
  /** @type {Record<string, boolean>} */
  const out = {};
  for (const row of PLANNING_SESSION_CREATE_FIELD_DEFS) {
    out[row.id] = row.required || DEFAULT_STANDARD_FIELD_IDS.has(row.id);
  }
  return out;
}

/**
 * @param {unknown} raw
 * @param {Record<string, boolean>|null|undefined} [visibility]
 * @returns {Record<string, boolean>}
 */
export function normalizePlanningSessionCreateStandardFields(raw, visibility) {
  const vis = normalizePlanningSessionCreateFieldVisibility(visibility);
  const defaults = defaultPlanningSessionCreateStandardFields();
  const source =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  /** @type {Record<string, boolean>} */
  const out = { ...defaults };
  for (const row of PLANNING_SESSION_CREATE_FIELD_DEFS) {
    if (row.required) {
      out[row.id] = true;
      continue;
    }
    if (vis[row.id] === false) {
      out[row.id] = false;
      continue;
    }
    if (source[row.id] === true) {
      out[row.id] = true;
      continue;
    }
    if (source[row.id] === false) {
      out[row.id] = false;
      continue;
    }
    out[row.id] = defaults[row.id] !== false;
  }
  return out;
}

/**
 * @param {string} fieldId
 * @param {Record<string, boolean>|null|undefined} standardFields
 * @param {Record<string, boolean>|null|undefined} visibility
 */
export function isPlanningSessionCreateFieldInStandard(
  fieldId,
  standardFields,
  visibility,
) {
  const id = asStr(fieldId);
  if (!id) return true;
  if (REQUIRED_IDS.has(id)) return true;
  const vis = normalizePlanningSessionCreateFieldVisibility(visibility);
  if (vis[id] === false) return false;
  const normalized = normalizePlanningSessionCreateStandardFields(
    standardFields,
    vis,
  );
  return normalized[id] !== false;
}

/**
 * @param {Record<string, boolean>|null|undefined} visibility
 * @param {Record<string, boolean>|null|undefined} standardFields
 */
export function createPlanningSessionCreateFieldDisplayChecker(
  visibility,
  standardFields,
) {
  const vis = normalizePlanningSessionCreateFieldVisibility(visibility);
  const standard = normalizePlanningSessionCreateStandardFields(
    standardFields,
    vis,
  );
  return {
    isVisible: (fieldId) => isPlanningSessionCreateFieldVisible(fieldId, vis),
    isStandard: (fieldId) =>
      isPlanningSessionCreateFieldInStandard(fieldId, standard, vis),
    showInMode: (fieldId, isIndividualMode = true) => {
      const id = asStr(fieldId);
      if (!id || vis[id] === false) return false;
      if (!isIndividualMode && standard[id] === false) return false;
      return true;
    },
  };
}

export function normalizePlanningSessionCreateFieldVisibility(raw) {
  const defaults = defaultPlanningSessionCreateFieldVisibility();
  const source =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  /** @type {Record<string, boolean>} */
  const out = { ...defaults };
  for (const row of PLANNING_SESSION_CREATE_FIELD_DEFS) {
    if (row.required) {
      out[row.id] = true;
      continue;
    }
    const val = source[row.id];
    if (val === false) out[row.id] = false;
    else if (val === true) out[row.id] = true;
  }
  return out;
}

/**
 * @param {string} fieldId
 * @param {Record<string, boolean>|null|undefined} visibility
 */
export function isPlanningSessionCreateFieldVisible(fieldId, visibility) {
  const id = asStr(fieldId);
  if (!id) return true;
  if (REQUIRED_IDS.has(id)) return true;
  const normalized = normalizePlanningSessionCreateFieldVisibility(visibility);
  return normalized[id] !== false;
}

/**
 * @param {Record<string, boolean>|null|undefined} visibility
 */
export function createPlanningSessionCreateFieldVisibilityChecker(visibility) {
  const normalized = normalizePlanningSessionCreateFieldVisibility(visibility);
  return (fieldId) => isPlanningSessionCreateFieldVisible(fieldId, normalized);
}

/**
 * @param {Record<string, boolean>|null|undefined} visibility
 */
export function groupPlanningSessionCreateFieldsForAdmin(
  visibility,
  standardFields,
) {
  const normalized = normalizePlanningSessionCreateFieldVisibility(visibility);
  const standard = normalizePlanningSessionCreateStandardFields(
    standardFields,
    normalized,
  );
  return PLANNING_SESSION_CREATE_SECTION_ORDER.map((sectionKey) => ({
    sectionKey,
    fields: PLANNING_SESSION_CREATE_FIELD_DEFS.filter(
      (row) => row.sectionKey === sectionKey,
    ).map((row) => ({
      ...row,
      visible: normalized[row.id] !== false,
      inStandard: standard[row.id] !== false,
    })),
  })).filter((section) => section.fields.length > 0);
}
